<script lang="ts">
  import { onMount } from 'svelte';
  import { cn } from '@/lib/utils';
  import TagWrapper from '@/Common/TagWrapper.svelte';
  import XSSDisplay from '@/Common/XSSDisplay.svelte';
  import Avatar from '@/Avatar.svelte';
  import DifficultyDiamonds from '@/DifficultyDiamonds.svelte';
  import { Activity } from '@/Common/ActivityFunctions';
  import { formatXSSDetails, handleImageError, kmToMile, useImperialUnits } from '@/Util/utils';
  import { format_duration, time_update_minute } from '@/time';
  import { activityCardTags, activityDateLabel, activityDetailsLink, activityImageUrl, type DashboardActivity } from './activityCardUtils';

  export let exercise: DashboardActivity;
  export let size: 'sm' | 'lg' = 'lg';
  /** Coach/squad dashboards list several athletes, so each card names whose ride it is. */
  export let showOwner: boolean = false;

  // @ts-ignore
  const jq = window.jQuery;
  const imperial = useImperialUnits();

  const medalLabels: Record<number, { label: string; classes: string }> = {
    [-1]: { label: 'Near BT', classes: 'bg-fake-br' },
    1: { label: 'Bronze BT', classes: 'bg-bronze-br' },
    2: { label: 'Silver BT', classes: 'bg-silver-br' },
    3: { label: 'Gold BT', classes: 'bg-gold-br' },
  };

  $: medal = medalLabels[exercise?.medal ?? 0] ?? null;
  $: tags = activityCardTags(exercise, 'small');
  $: dateLabel = activityDateLabel(exercise, $time_update_minute);
  $: duration = exercise?.duration ?? exercise?.adu;
  $: durationStr = duration ? format_duration(duration) : null;
  $: distance = exercise?.distance ? Math.round((imperial ? kmToMile(exercise.distance) : exercise.distance) * 10) / 10 : 0;
  $: avgPower = exercise?.avg_power ? Math.round(exercise.avg_power) : null;
  $: difficulty = exercise?.difficulty ?? null;
  $: focusLabel = [Activity.getSpecRating(exercise), exercise?.focus].filter(Boolean).join(' ');
  $: xssDetails = formatXSSDetails({
    xss: exercise?.xss ?? 0,
    xlss: exercise?.xlss ?? 0,
    xhss: exercise?.xhss ?? 0,
    xpss: exercise?.xpss ?? 0,
  });
  $: detailsLink = activityDetailsLink(exercise);
  $: owner = exercise?.user;

  onMount(() => {
    jq('[data-toggle=tooltip]').tooltip({ container: '#shadcn-ui-dialog-target' });
  });
</script>

<div class={cn('flex flex-col gap-1', size === 'lg' ? 'w-72' : 'w-48', 'text-sm')}>
  <div class={cn('flex flex-col', size === 'lg' ? '' : 'h-[44px]')}>
    <h3 class={cn('!whitespace-normal w-full font-medium m-0', size === 'lg' ? 'text-base line-clamp-1' : 'text-sm line-clamp-2')}>
      <a class="text-foreground hover:underline" href={detailsLink} target="activity" on:click={(e) => e.stopPropagation()}>
        {exercise.name}
      </a>
    </h3>
    <div class="text-[11px] text-muted-foreground truncate" title={dateLabel}>{dateLabel}</div>
    {#if showOwner && owner?.username}
      <div class="flex flex-row items-center gap-1.5 mt-1 min-w-0">
        <Avatar
          shape="circle"
          pad_right={false}
          username={owner.username}
          first_name={owner.first_name ?? ''}
          last_name={owner.last_name ?? ''}
          profile_photo={owner.profile_photo ?? ''}
          --avatar-height="18px"
        />
        <span class="text-[11px] text-muted-foreground truncate">{owner.first_name} {owner.last_name}</span>
      </div>
    {/if}
  </div>

  <div class="relative">
    <img
      class="w-full rounded-md border border-border"
      src={activityImageUrl(exercise)}
      alt="Activity"
      draggable="false"
      on:error={handleImageError}
    />
    {#if medal}
      <span class={cn('absolute top-1.5 left-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium text-white shadow-sm', medal.classes)}>
        <i class="fa-solid fa-award mr-1" aria-hidden="true"></i>{medal.label}
      </span>
    {/if}
    {#if exercise?.favorite}
      <span class="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-white/90 text-amber-500 text-[11px] flex items-center justify-center shadow-sm">
        <i class="fa-solid fa-star" aria-hidden="true"></i>
      </span>
    {/if}
  </div>

  <div class={cn('mt-1 flex flex-col gap-2', size === 'lg' ? 'text-sm' : 'text-xs')}>
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 min-w-0">
        {#if durationStr}
          <span class="inline-flex items-center gap-1 text-gray-700"><i class="fa-regular fa-clock text-[11px] text-gray-500" aria-hidden="true"></i>{durationStr}</span>
        {/if}
        {#if distance > 0}
          <span class="inline-flex items-center gap-1 text-gray-700"><i class="fa-regular fa-road text-[11px] text-gray-500" aria-hidden="true"></i>{distance} {imperial ? 'mi' : 'km'}</span>
        {:else if avgPower}
          <span data-toggle="tooltip" title="Average Power" class="inline-flex items-center gap-1 text-gray-700"><i class="fa-regular fa-gauge text-[11px] text-gray-500" aria-hidden="true"></i>{avgPower} W</span>
        {/if}
      </div>
      <TagWrapper tags={tags.slice(0, 1)} />
    </div>

    <div class="flex items-center gap-2 w-full min-w-0 overflow-hidden">
      <div class="flex items-center gap-3 flex-1 min-w-0">
        {#if difficulty}
          <div class="flex items-center gap-1 shrink-0" title={`Difficulty ${Math.round(difficulty)}`}>
            <DifficultyDiamonds {difficulty} compact={true} />
          </div>
        {/if}
        {#if size === 'lg' || !difficulty}
          <div class="flex items-center gap-1 flex-1 basis-0 min-w-0 overflow-hidden">
            <span class="truncate block max-w-full" title={focusLabel}>{focusLabel}</span>
          </div>
        {/if}
      </div>
      {#if exercise?.xss}
        <div class="flex items-center gap-1 shrink-0">
          <XSSDisplay xss={xssDetails} className={cn('font-semibold', size === 'lg' ? 'text-sm' : 'text-xs')} />
        </div>
      {/if}
    </div>
  </div>
</div>
